/**
 * Reassign Positions Dialog
 *
 * Moves positions from one Role Master entry to another,
 * optionally updating F&O position Job/Title assignments.
 * Pattern mirrors ReassignJobsDialog.tsx.
 */

import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useConfirm } from '@/hooks/useConfirm';
import roleMasterService from '@/services/api/roleMasterService';
import type {
  RoleMasterEntry, RoleMasterBrief, PositionBrief, ReassignPositionsResponse,
} from '@/services/api/roleMasterService';

const ReassignPositionsDialog: React.FC<{
  sourceEntry: RoleMasterEntry;
  onClose: () => void;
}> = ({ sourceEntry, onClose }) => {
  const { confirm: showConfirm } = useConfirm();
  const [positions, setPositions] = useState<PositionBrief[]>([]);
  const [allTargets, setAllTargets] = useState<RoleMasterBrief[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<Set<number>>(new Set());
  const [targetEntryId, setTargetEntryId] = useState<number | null>(null);
  const [updateFo, setUpdateFo] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<ReassignPositionsResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [posData, briefData] = await Promise.all([
        roleMasterService.getPositions(sourceEntry.id),
        roleMasterService.listBrief('active'),
      ]);
      setPositions(posData.positions);
      setSelectedPositions(new Set(posData.positions.map(p => p.id)));
      setAllTargets(briefData.filter(e => e.id !== sourceEntry.id));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const targetEntry = allTargets.find(t => t.id === targetEntryId);
  const canUpdateFo = !!(sourceEntry.fo_job_id && targetEntry?.fo_job_id);

  const handleExecute = async () => {
    if (selectedPositions.size === 0 || !targetEntryId) return;
    const target = allTargets.find(t => t.id === targetEntryId);
    if (!target) return;

    const confirmed = await showConfirm(
      `Reassign ${selectedPositions.size} position(s) from "${sourceEntry.title}" to "${target.title}"?${
        updateFo && canUpdateFo ? `\n\nF&O positions will be updated to Job: ${target.fo_job_id}` : ''
      }\n\nThis cannot be automatically undone.`,
      'Reassign Positions',
      { confirmText: `Reassign ${selectedPositions.size} Positions`, type: 'warning' }
    );
    if (!confirmed) return;

    setIsExecuting(true);
    try {
      const res = await roleMasterService.reassignPositions(sourceEntry.id, {
        target_entry_id: targetEntryId,
        position_ids: Array.from(selectedPositions),
        update_fo: updateFo && canUpdateFo,
      });
      setResult(res);
    } catch (error) {
      console.error('Reassign failed:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  const filteredTargets = searchTerm
    ? allTargets.filter(t =>
        t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.role_code.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allTargets;

  // Result screen
  if (result) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Reassignment Complete</DialogTitle>
        </DialogHeader>
        <div className="bg-emerald-50 rounded p-4 text-center">
          <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
          <p className="font-medium">{result.db_updated} positions reassigned</p>
          {result.fo_updated > 0 && <p className="text-sm text-gray-600">{result.fo_updated} F&O positions updated</p>}
          {result.fo_failed > 0 && <p className="text-red-600 text-sm">{result.fo_failed} F&O updates failed</p>}
          {result.fo_skipped > 0 && <p className="text-gray-500 text-sm">{result.fo_skipped} F&O updates skipped (no F&O position ID)</p>}
        </div>
        <DialogFooter><Button onClick={onClose}>Done</Button></DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Reassign Positions from "{sourceEntry.title}"</DialogTitle>
        <DialogDescription>
          Select positions to move to a different Role Master entry. {sourceEntry.role_code} currently has {positions.length} linked positions.
        </DialogDescription>
      </DialogHeader>

      {isLoading ? (
        <div className="py-8 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading positions...</div>
      ) : (
        <div className="space-y-4">
          {/* Target selector */}
          <div>
            <label className="text-sm font-medium block mb-2">Target Role:</label>
            <Input
              placeholder="Search by title or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-2"
            />
            <div className="max-h-[150px] overflow-y-auto border rounded">
              {filteredTargets.length === 0 ? (
                <div className="text-center py-3 text-gray-400 text-sm">No matching roles</div>
              ) : (
                filteredTargets.map(t => (
                  <div
                    key={t.id}
                    className={`flex items-center justify-between px-3 py-1.5 cursor-pointer text-sm hover:bg-gray-50 ${
                      targetEntryId === t.id ? 'bg-indigo-50 border-l-2 border-indigo-500' : ''
                    }`}
                    onClick={() => setTargetEntryId(t.id)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs text-gray-400 flex-shrink-0">{t.role_code}</span>
                      <span className="truncate">{t.title}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {t.fo_job_id && <span className="text-[10px] text-emerald-600 font-mono">F&O</span>}
                      <span className="text-xs text-gray-400">{t.linked_positions_count} pos</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* F&O sync option */}
          {targetEntry && (
            <div className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                id="update_fo_check"
                checked={updateFo && canUpdateFo}
                onChange={e => setUpdateFo(e.target.checked)}
                disabled={!canUpdateFo}
                className="rounded border-gray-300"
              />
              <label htmlFor="update_fo_check" className={canUpdateFo ? 'text-gray-700' : 'text-gray-400'}>
                Also update positions in F&O
              </label>
              {!canUpdateFo && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {!sourceEntry.fo_job_id ? 'Source' : 'Target'} has no F&O job linked
                </span>
              )}
            </div>
          )}

          {/* Select all / deselect all */}
          <div className="flex items-center gap-3 text-xs">
            <button className="text-blue-600 hover:underline" onClick={() => setSelectedPositions(new Set(positions.map(p => p.id)))}>
              Select all ({positions.length})
            </button>
            <button className="text-blue-600 hover:underline" onClick={() => setSelectedPositions(new Set())}>
              Deselect all
            </button>
            <span className="text-gray-500">{selectedPositions.size} of {positions.length} selected</span>
          </div>

          {/* Positions table */}
          <div className="max-h-[35vh] overflow-y-auto border rounded">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs w-8"></TableHead>
                <TableHead className="text-xs">Code</TableHead>
                <TableHead className="text-xs">Title</TableHead>
                <TableHead className="text-xs">Campus</TableHead>
                <TableHead className="text-xs">Incumbent</TableHead>
                <TableHead className="text-xs">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {positions.map(pos => (
                  <TableRow key={pos.id} className={selectedPositions.has(pos.id) ? '' : 'opacity-40'}>
                    <TableCell className="py-1 px-2">
                      <input
                        type="checkbox"
                        checked={selectedPositions.has(pos.id)}
                        className="rounded"
                        onChange={e => {
                          const next = new Set(selectedPositions);
                          e.target.checked ? next.add(pos.id) : next.delete(pos.id);
                          setSelectedPositions(next);
                        }}
                      />
                    </TableCell>
                    <TableCell className="text-xs py-1 font-mono">{pos.position_code}</TableCell>
                    <TableCell className="text-xs py-1 font-medium">{pos.title}</TableCell>
                    <TableCell className="text-xs py-1 text-gray-500">{pos.campus_name || '—'}</TableCell>
                    <TableCell className="text-xs py-1 text-gray-500">{pos.incumbent_name || '—'}</TableCell>
                    <TableCell className="text-xs py-1 text-gray-500">{pos.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleExecute}
          disabled={isExecuting || selectedPositions.size === 0 || !targetEntryId}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          {isExecuting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Reassign {selectedPositions.size} Positions
        </Button>
      </DialogFooter>
    </>
  );
};

export default ReassignPositionsDialog;
